import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { OutfitRowData } from '../../types/admin';

export interface BulkUploadButtonProps {
  rows: OutfitRowData[];
  hasSelectedUser: boolean;
  isUploading: boolean;
  onUpload: () => void;
  onValidationErrors: (errors: Map<string, string[]>) => void;
}

/**
 * Validates all rows before bulk upload.
 * Every row must have a non-empty event type AND at least one clothing item
 * with type, brand, and color filled in.
 */
export function validateRows(rows: OutfitRowData[]): Map<string, string[]> {
  const errors = new Map<string, string[]>();
  for (const row of rows) {
    const rowErrors: string[] = [];
    if (!row.eventType) {
      rowErrors.push('Event type is required');
    }
    const hasCompleteItem = row.clothingItems.some(
      item => item.type && item.brand && item.color
    );
    if (!hasCompleteItem) {
      rowErrors.push('At least one clothing item with type, brand, and color is required');
    }
    if (rowErrors.length > 0) {
      errors.set(row.id, rowErrors);
    }
  }
  return errors;
}

const BulkUploadButton: React.FC<BulkUploadButtonProps> = ({
  rows,
  hasSelectedUser,
  isUploading,
  onUpload,
  onValidationErrors,
}) => {
  const isDisabled = rows.length === 0 || !hasSelectedUser || isUploading;

  const handleClick = () => {
    const errors = validateRows(rows);
    if (errors.size > 0) {
      onValidationErrors(errors);
      return;
    }
    onUpload();
  };

  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      disabled={isDisabled}
      onClick={handleClick}
      startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
    >
      {isUploading ? 'Uploading...' : `Bulk Upload (${rows.length})`}
    </Button>
  );
};

export default BulkUploadButton;
